"use client";
import React, { useState, useEffect } from "react";
import Link from "next/link";
import Paging from "@/components/common/paging/Paging";
import { DEFAULT_PAGE, NEXT_PAGE_NUMBER } from "@/constants/appConstant";
import { getPostsByTopicUrlName } from "@/app/api/post/postAPI";
import OptimizedImage from '@/components/common/optimizedImage/OptimizedImage';

const NewsClient = ({ urlName, initialPosts, initialHasNext, initialTotal }) => {
    const [posts, setPosts] = useState(initialPosts);
    const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
    const [hasNext, setHasNext] = useState(initialHasNext);
    const [totalElements, setTotalElements] = useState(initialTotal);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    useEffect(() => {
        if (currentPage === DEFAULT_PAGE) return;

        let isActive = true;
        const loadMorePosts = async () => {
            setIsLoadingMore(true);
            const response = await getPostsByTopicUrlName(urlName, currentPage);
            if (!isActive) return;

            const nextPosts = response?.data?.data?.content || [];

            setPosts((prevPosts) => {
                const mergedPosts = [...prevPosts, ...nextPosts];
                const uniquePosts = new Map();

                mergedPosts.forEach((post, index) => {
                    const safeKey = post?.id || post?.slug || post?.urlName || `news-${index}`;
                    if (!uniquePosts.has(safeKey)) {
                        uniquePosts.set(safeKey, post);
                    }
                });
                return Array.from(uniquePosts.values());
            });

            setHasNext(Boolean(response?.data?.data?.hasNext));
            setTotalElements(response?.data?.data?.totalElements || 0);
            setIsLoadingMore(false);
        };

        loadMorePosts();

        return () => {
            isActive = false;
        };
    }, [currentPage, urlName]);

    const handleLoadMore = () => {
        if (isLoadingMore || !hasNext) return;
        setCurrentPage((page) => page + NEXT_PAGE_NUMBER);
    };

    if (!posts || posts.length === 0) {
        return (
            <div className="container-fluid text-center mt-7">
                <p>Không có bài viết nào.</p>
            </div>
        );
    }

    return (
        <div className="container-fluid block-content mb-0 mt-7">
            <div className="row main-grid flex justify-content-center">
                <div className="col-sm-9">
                    <div className="row services">
                        {posts.map((post, index) => {
                            const safeKey = post?.id || post?.slug || post?.urlName || `news-${index}`;

                            return (
                                <div key={safeKey} className="service-item col-xs-12 col-sm-4 isotope-item mb-4">
                                    <div className="img-service">
                                        <Link href={post.urlName}>
                                            <OptimizedImage
                                                src={post.titleImage}
                                                alt={post.name}
                                                width={360}
                                                height={270}
                                                className="full-width"
                                                unoptimized={true}
                                                priority={index < 3}
                                                loading={index < 3 ? undefined : "lazy"}
                                                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                            />
                                        </Link>
                                    </div>
                                    <Link className="no-underline" href={post.urlName}>
                                        <h2 className="name-post" style={{ fontSize: "16px" }}>
                                            {post?.name}
                                        </h2>
                                    </Link>
                                    <p className="description-post">{post.description}</p>
                                    <div className="text-center">
                                        <Link className="btn btn-brand m-3 align-items-center" href={post.urlName}>
                                            Chi tiết
                                        </Link>
                                    </div>
                                </div>
                            );
                        })}

                        <Paging
                            onLoadMore={handleLoadMore}
                            totalElements={totalElements}
                            hasNext={hasNext}
                            data={posts}
                            isLoading={isLoadingMore}
                        />
                    </div>
                </div>
            </div>
        </div >
    );
};

export default NewsClient;